import React, {FC, useState} from 'react'

import Page from 'src/designSystem/components/organisms/Page/Page'
import Box from 'src/designSystem/components/atoms/Box/Box'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {
  RadioButtonList,
  RadioButtonListOption,
} from 'src/designSystem/components/molecules/RadioButtonList/RadioButtonList'
import {SurveyItemData} from 'src/products/general/MilitarySurvey/MilitarySurvey.types'
import {useTranslation} from 'react-i18next'

type MilitarySurveyTemplateProps = {
  onContinue: (isMilitary: boolean) => Promise<void>
}

const MilitarySurveyTemplate: FC<MilitarySurveyTemplateProps> = ({onContinue}) => {
  const {t} = useTranslation(['MilitarySurvey', 'Common'])

  const answerOptions: RadioButtonListOption[] = [
    {id: 'Yes', text: t('Common:Yes')},
    {id: 'No', text: t('Common:No')},
  ]

  const initialSurveyData: SurveyItemData[] = [
    {
      questionId: 'military-question-1',
      questionText: t('MilitaryQuestion1'),
      answerOptions,
      selectedAnswer: undefined,
    },
    {
      questionId: 'military-question-2',
      questionText: t('MilitaryQuestion2'),
      answerOptions,
      selectedAnswer: undefined,
    },
    {
      questionId: 'military-question-3',
      questionText: t('MilitaryQuestion3'),
      answerOptions,
      selectedAnswer: undefined,
    },
    {
      questionId: 'military-question-4',
      questionText: t('MilitaryQuestion4'),
      answerOptions,
      selectedAnswer: undefined,
    },
  ]

  const [isBusy, setIsBusy] = useState(false)
  const [surveyData, setSurveyData] = useState<SurveyItemData[]>(initialSurveyData)

  const checkIfMilitary = (): boolean => {
    return surveyData.some((surveyItem) => surveyItem.selectedAnswer === 'Yes')
  }

  const handleOnPressContinue = async (): Promise<void> => {
    setIsBusy(true)

    const isMilitary = checkIfMilitary()

    try {
      await onContinue(isMilitary)
    } finally {
      setIsBusy(false)
    }
  }

  const handleOnSelect = (currIndex: number, answerId: string): void => {
    if (answerId !== 'Yes' && answerId !== 'No') return
    setSurveyData((prevSurveyData) =>
      prevSurveyData.map((surveyItem, surveyItemIndex) =>
        currIndex === surveyItemIndex ? {...surveyItem, selectedAnswer: answerId} : surveyItem,
      ),
    )
  }
  const renderSurveyItems = (): JSX.Element => {
    return (
      <>
        {surveyData.map((surveyItem, surveyItemIndex) => (
          <Box key={surveyItem.questionId} marginBottom="medium">
            <PFText variant="p">{surveyItem.questionText}</PFText>
            <RadioButtonList
              options={surveyItem.answerOptions}
              onPress={(answerId) => handleOnSelect(surveyItemIndex, answerId)}
              selectedOption={surveyItem.selectedAnswer ?? ''}
            />
          </Box>
        ))}
      </>
    )
  }

  const isDisabled = (): boolean => {
    for (const surveyItem of surveyData) {
      if (surveyItem.selectedAnswer === undefined) return true
    }
    return false
  }

  return (
    <Page
      variant="generic"
      smallTopGap={true}
      testID="MilitarySurveyPage"
      title={t('Title')}
      buttonProps={{
        type: 'singleButton',
        primary: {
          text: t('Common:Next'),
          onPress: handleOnPressContinue,
          disabled: isDisabled() || isBusy,
          testID: 'MilitarySurvey-NextBtn',
        },
      }}
    >
      {renderSurveyItems()}
    </Page>
  )
}

export {MilitarySurveyTemplate}
